import { cloneDeep } from "lodash";
import { UPDATE_MESSAGE_STATUS, GET_CONVERSATION_LIST, GET_CONVERSATION_MESSAGE_HISOTRY, INITIATE_CONVERSATION, SEND_MESSAGE, HANDLE_INCOMING_MESSAGE, HANDLE_READ_MESSAGE_CALLBACK, HANDLE_RECEIVER_READ_MESSAGE, HANDLE_MESSAGE_EDIT, HANDLE_MESSAGE_REMOVE, HANDLE_STATUS_CHANGE } from "store/actions/clientMessaging";

const initialState = {
  conversationList: [],
};

const initiateConversation = (state, payload) => {
  const { data, userId, callback } = payload;
  if (typeof callback === 'function') {
    callback(data.body);
  }

  const conversation = data?.body;
  if(!conversation) {
    return state;
  }

  const newConversation = {
    conversation_id: conversation.id,
    latest_message_at: null,
    client_id: conversation.client_id,
    unread_count: 0,
    latest_message_id: null,
    sender_id: conversation.pract_id,
    receiver_id: conversation.client_id,
    message: "",
    created_at: null,
    client_first_name: conversation.first_name,
    client_last_name: conversation.last_name,
  }

  const findExisting = state.conversationList.find(conversation => conversation.conversation_id === newConversation.conversation_id);
  if(findExisting) {
    return state;
  }

  const cloned = cloneDeep(state.conversationList);
  cloned.push(newConversation);

  const stateObj = {
    ...state,
    conversationList: cloned,
  };
  return stateObj;
}

const getConversationList = (state, payload) => {
  const { data, callback } = payload;
  if (typeof callback === 'function') {
    callback(data.body);
  }
  const stateObj = {
    ...state,
    conversationList: data?.body?.data,
  };
  return stateObj;
}

const getConversationMessageHistory = (state, payload) => {
  const { data, conversationId, lastMessageId, callback } = payload;
  if (typeof callback === 'function') {
    callback(data.body);
  }

  let newMessages = data?.body?.data;
  if (!Array.isArray(newMessages) || newMessages.length === 0) {
    return state;
  }

  newMessages = newMessages.reverse();

  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state;
  }

  const conversation = updatedConversations[index];

  // replace existing messages if not a pagniated request
  if(!lastMessageId) {
    conversation.messages = newMessages;  
  } else {
    const existingMessages = conversation.messages || [];
    conversation.messages = [...newMessages, ...existingMessages];
  }

  conversation.totalMessagesCount = data?.body?.total_count;

  updatedConversations[index] = conversation;

  return {
    ...state,
    conversationList: updatedConversations,
  };
}

const sendMessageToClient = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const updatedConversations = addMessageToConversation(state, data, "pending");

  return {
    ...state,
    conversationList: updatedConversations,
  };
};

const updateMessageStatus = (state, payload) => {
  const { data, status, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const conversationId = data?.conversation_id;
  const idempotencyId = data?.idempotencyId;
  const messageId = data?.messageId;

  if (!conversationId || !idempotencyId || !messageId) {
    return state;
  }

  // Clone conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state; // Conversation not found
  }

  const conversation = updatedConversations[index];
  const existingMessages = conversation.messages || [];

  // Find the message by idempotencyId
  const messageIndex = existingMessages.findIndex(
    (msg) => msg.idempotencyId === idempotencyId
  );

  if (messageIndex === -1) {
    return state; // Message not found
  }

  // Update the message status and ID
  existingMessages[messageIndex] = {
    ...existingMessages[messageIndex],
    status,
    id: messageId,
  };

  // Update messages in conversation
  conversation.messages = existingMessages;

  // update latest conversation message id
  if(messageIndex === existingMessages?.length - 1) {
    conversation.latest_message_id = messageId;
  }

  // Return updated state
  return {
    ...state,
    conversationList: updatedConversations,
  };
};

const handleIncomingMessage = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const updatedConversations = addMessageToConversation(state, data, "", true);

  return {
    ...state,
    conversationList: updatedConversations,
  };
};

const addMessageToConversation = (state, message, status, isIncoming = false) => {
  const conversationId = message?.conversation_id;
  const idempotencyId = message?.idempotencyId;

  if (!conversationId || !idempotencyId || !message) {
    return state.conversationList;
  }

  // Clone the conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );
  
  if (index === -1) {
    return state.conversationList; // Conversation not found
  }

  const conversation = updatedConversations[index];
  const existingMessages = conversation.messages || [];

  // Prevent duplicate message by idempotencyId
  const isDuplicate = existingMessages.some(
    (msg) => msg.idempotencyId === idempotencyId
  );

  if (isDuplicate) {
    return state.conversationList; // Don't add duplicate
  }

  // Append new message
  conversation.messages = [
    ...existingMessages,
    {
      ...message,
      id: message?.messageId,
      status,
    }
  ];

  // if message is incoming, increase current conversation unread msg count
  if(isIncoming) {
    conversation.unread_count = (conversation.unread_count || 0) + 1;
  }

  conversation.message = message?.message;
  conversation.latest_message_at = new Date().toISOString();
  conversation.is_latest_message_deleted = false;

  return updatedConversations;
}

const handleMsgReadCallback = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const conversationId = data?.conversation_id;
  // const msgIds = data?.msg_ids?.length && data?.msg_ids.map(id => Number(id));
  const newUnreadCount = data?.unread_count;

  if (!conversationId) {
    return state;
  }

  // Clone conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state; // Conversation not found
  }

  const conversation = updatedConversations[index];

  // update new unread count of conversation
  conversation.unread_count = newUnreadCount;

  // if(msgIds?.length) {
  //   conversation.messages = conversation.messages.map(m => {
  //     if(msgIds.includes(Number(m.id))) {
  //       m.is_read = true;
  //     }
  //     return m;
  //   })
  // }

  // Return updated state
  return {
    ...state,
    conversationList: updatedConversations,
  };
};

const handleReceiverMsgRead = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const conversationId = data?.conversation_id;
  const msgIds = data?.msg_ids?.length && data?.msg_ids.map(id => Number(id));

  if (!conversationId || !msgIds) {
    return state;
  }

  // Clone conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state; // Conversation not found
  }

  const conversation = updatedConversations[index];
  if(!conversation?.messages?.length) {
    return;
  }

  // update messages read status
  conversation.messages = conversation.messages.map(m => {
    if(msgIds.includes(Number(m.id))) {
      m.is_read = true;
    }
    return m;
  });

  // Return updated state
  return {
    ...state,
    conversationList: updatedConversations,
  };
}

const handleEditMessage = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const conversationId = data?.conversation_id;
  const messageId = data?.message_id;
  const editedMessage = data?.message;
  const editedAt = data?.edited_at || new Date();

  if (!conversationId || !messageId || !editedMessage) {
    return state;
  }

  // Clone conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state; // Conversation not found
  }

  const conversation = updatedConversations[index];
  
  // Update the message in the conversation
  conversation.messages = conversation.messages.map(m => {
    if (m.id === messageId) {
      return { ...m, message: editedMessage, edited_at: editedAt };
    }
    return m;
  });
  
  if(conversation?.latest_message_id === messageId) {
    conversation.message = editedMessage;
  }

  console.log("Updated conversation >>>", conversation);

  // Return updated state
  return {
    ...state,
    conversationList: updatedConversations,
  };
}

const handleRemoveMessage = (state, payload) => {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }

  const conversationId = data?.conversation_id;
  const messageId = data?.message_id;
  const deletedAt = data?.deleted_at || new Date();

  if (!conversationId || !messageId) {
    return state;
  }

  // Clone conversation list
  const updatedConversations = cloneDeep(state.conversationList);
  const index = updatedConversations.findIndex(
    (conv) => conv.conversation_id === conversationId
  );

  if (index === -1) {
    return state; // Conversation not found
  }

  const conversation = updatedConversations[index];

  // Remove the message from the conversation
  conversation.messages = conversation.messages.map(m => {
    if (m.id === messageId) {
      return { ...m, is_deleted: true, deleted_at: deletedAt };
    }
    return m;
  });

  if(conversation?.latest_message_id === messageId) {
    conversation.is_latest_message_deleted = true;
    conversation.message = null;
  }

  // Return updated state
  return {
    ...state,
    conversationList: updatedConversations,
  };
}

function handleStatusChange(state, payload) {
  const { data, callback } = payload;

  if (typeof callback === 'function') {
    callback(data);
  }
  const { status, user_id } = data;

  const conversationList = state.conversationList.map(conversation => {

    if (conversation.client_id === user_id) {
      return {
        ...conversation,
        active_status: status === "online" ? true : false,
      };
    }
    
    return conversation;
  });

  return {
    ...state,
    conversationList,
  };
}


const ClientMessaging = (state = initialState, { type, payload = null }) => {
  switch (type) {
      case INITIATE_CONVERSATION:
        return initiateConversation(state, payload);
      case GET_CONVERSATION_LIST:
        return getConversationList(state, payload);
      case GET_CONVERSATION_MESSAGE_HISOTRY:
        return getConversationMessageHistory(state, payload);
      case SEND_MESSAGE:
        return sendMessageToClient(state, payload);
      case UPDATE_MESSAGE_STATUS:
        return updateMessageStatus(state, payload);        
      case HANDLE_INCOMING_MESSAGE:
        return handleIncomingMessage(state, payload);
      case HANDLE_READ_MESSAGE_CALLBACK:
        return handleMsgReadCallback(state, payload);
      case HANDLE_RECEIVER_READ_MESSAGE:
        return handleReceiverMsgRead(state, payload);
      case HANDLE_MESSAGE_EDIT:
        return handleEditMessage(state, payload);
      case HANDLE_MESSAGE_REMOVE:
        return handleRemoveMessage(state, payload);
      case HANDLE_STATUS_CHANGE:
        return handleStatusChange(state, payload)
      default:
        return state;
  };
}
export default ClientMessaging;